import { useState, type ReactNode } from 'react';

import { ChatThread, DockedQuestionCard } from '@/components/rooms';
import { Button } from '@/components/ui/shadcn';

import { COPY } from '../copy';
import { PANEL_CLICK_GUARD_MS, useClickGuard } from '../hooks/useClickGuard';
import { hasResumeDecision, recordResumeDecision } from '../state/resumeDecision';
import { browserStorage, browserTabStorage, clearStoredSession, readStoredSession } from '../state/session';
import { OnboardingShell, type ShellOperator } from './OnboardingShell';

export interface ResumeGateProps {
  /** The signed-in operator's identity: only their own stored session counts. */
  ownerKey: string | null;
  operator?: ShellOperator;
  /** Injected in tests. */
  storage?: Storage | null;
  tabStorage?: Storage | null;
  now?: () => number;
  clickGuardMs?: number;
  /** The chat. It mounts only after the answer, so nothing reattaches or restarts the old session before it. */
  children: ReactNode;
}

/**
 * Coming back to an onboarding still in progress asks first: continue it, or start over. Asked once per sign-in in a
 * tab, so a reload never asks. Starting over forgets the stored session: a new id runs every job again, same site or not.
 */
export function ResumeGate({
  ownerKey,
  operator,
  storage = browserStorage(),
  tabStorage = browserTabStorage(),
  now = Date.now,
  clickGuardMs = PANEL_CLICK_GUARD_MS,
  children,
}: ResumeGateProps) {
  const [pendingSite, setPendingSite] = useState<string | null>(() => {
    const stored = readStoredSession(storage, now(), ownerKey);
    const inProgress = stored?.url && stored.completedAt === null ? stored.url : null;
    if (inProgress && !hasResumeDecision(tabStorage)) return inProgress;
    recordResumeDecision(tabStorage);
    return null;
  });
  const guard = useClickGuard(clickGuardMs);

  if (pendingSite === null) return <>{children}</>;

  const decide = (startOver: boolean) => {
    if (startOver) clearStoredSession(storage);
    recordResumeDecision(tabStorage);
    setPendingSite(null);
  };

  return (
    <OnboardingShell operator={operator ?? (ownerKey ? { email: ownerKey } : undefined)} roomStatus="open">
      <ChatThread
        dock={
          <DockedQuestionCard
            focusHeading
            title={COPY.resumeTitle(pendingSite)}
            subtitle={COPY.resumeSubtitle}
            footer={
              <>
                <span className="flex-1" />
                <Button type="button" variant="ghost" size="xs" onClick={guard(() => decide(true))}>
                  {COPY.resumeStartOver}
                </Button>
                <Button type="button" size="xs" onClick={guard(() => decide(false))}>
                  {COPY.resumeContinue}
                </Button>
              </>
            }
          />
        }
      >
        {null}
      </ChatThread>
    </OnboardingShell>
  );
}
